import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { $, on } from "./dom";

/** Passkey register/login wiring + global logout. Progressive enhancement only. */

async function postJson(url: string, body?: unknown): Promise<Response> {
  return fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

function friendlyError(err: unknown): string {
  const name = (err as { name?: string })?.name;
  if (name === "NotAllowedError") return "Passkey prompt was cancelled or timed out.";
  if (name === "InvalidStateError") return "This passkey is already registered.";
  if (err instanceof Error && err.message) return err.message;
  return "Something went wrong. Please try again.";
}

async function register(handle: string, invite: string): Promise<void> {
  const optsRes = await postJson("/auth/register/options", { handle, invite });
  const optionsJSON = await optsRes.json();
  if (!optsRes.ok) throw new Error(optionsJSON.error ?? "Could not start registration.");

  const attResp = await startRegistration({ optionsJSON });
  const verifyRes = await postJson("/auth/register/verify", attResp);
  const result = await verifyRes.json();
  if (!verifyRes.ok || !result.ok) throw new Error(result.error ?? "Registration failed.");
}

async function login(handle: string): Promise<void> {
  const optsRes = await postJson("/auth/login/options", { handle });
  const optionsJSON = await optsRes.json();
  if (!optsRes.ok) throw new Error(optionsJSON.error ?? "Could not start sign-in.");

  const asseResp = await startAuthentication({ optionsJSON });
  const verifyRes = await postJson("/auth/login/verify", asseResp);
  const result = await verifyRes.json();
  if (!verifyRes.ok || !result.ok) throw new Error(result.error ?? "Sign-in failed.");
}

function initLoginPage(): void {
  const form = $("#auth-form") as HTMLFormElement | null;
  if (!form) return;
  const handleInput = $("#auth-handle") as HTMLInputElement | null;
  const inviteInput = $("#auth-invite") as HTMLInputElement | null;
  const errorEl = $("#auth-error");
  const statusEl = $("#auth-status");
  const registerBtn = $("#auth-register");

  const run = async (mode: "login" | "register") => {
    const handle = handleInput?.value.trim() ?? "";
    if (handle.length < 3) {
      if (errorEl) errorEl.textContent = "Enter a handle (3+ characters).";
      return;
    }
    const invite = inviteInput?.value.trim() ?? "";
    if (mode === "register" && !invite) {
      if (errorEl) errorEl.textContent = "An invite code is required to create an account.";
      return;
    }
    if (errorEl) errorEl.textContent = "";
    if (statusEl)
      statusEl.textContent =
        mode === "register" ? "Creating your passkey…" : "Waiting for your passkey…";
    try {
      await (mode === "register" ? register(handle, invite) : login(handle));
      window.location.href = "/dashboard";
    } catch (err) {
      if (statusEl) statusEl.textContent = "";
      if (errorEl) errorEl.textContent = friendlyError(err);
    }
  };

  on(form, "submit", (e) => {
    e.preventDefault();
    void run("login");
  });
  on(registerBtn, "click", () => void run("register"));
}

function initLogout(): void {
  for (const btn of document.querySelectorAll('[data-action="logout"]')) {
    on(btn, "click", async () => {
      await postJson("/auth/logout");
      window.location.href = "/";
    });
  }
}

/** Register an additional passkey for the already-signed-in user (from the dashboard). */
export async function addPasskey(): Promise<{ ok: boolean; error?: string }> {
  try {
    const optsRes = await postJson("/auth/register/options");
    const optionsJSON = await optsRes.json();
    if (!optsRes.ok) return { ok: false, error: optionsJSON.error ?? "Could not start." };
    const attResp = await startRegistration({ optionsJSON });
    const verifyRes = await postJson("/auth/register/verify", attResp);
    const result = await verifyRes.json();
    if (!verifyRes.ok || !result.ok) return { ok: false, error: result.error ?? "Failed." };
    return { ok: true };
  } catch (err) {
    return { ok: false, error: friendlyError(err) };
  }
}

export function initAuth(): void {
  initLogout();
  initLoginPage();
}
